import React from 'react';
import { ArtistSubscriptionFragment, DiscountTypeEnum } from '@minecraft.graphql-operations';
import Modal from '@minecraft.modal';
import {
  BasicContainer,
  BodyTextLarge,
  BodyTextSmall,
  FlexCol,
  FlexRow,
  H1Text,
  H3Text,
  PaywallButton,
  TextDynamic,
} from '@minecraft.atoms';
import IconWithErrorBoundary from '@minecraft.icon';
import { gqlSubscription } from '@minecraft.graphql-types';
import { Trans, useTranslation } from '../../I18N';
import { PaywallLocation } from '../enums/PaywallLocation';
import { PlanType } from '../usePlans';
import { useDiscountHook } from './useDiscountHook';

type Props = {
  showBillingPopup: (data: { location?: PaywallLocation; discountType?: DiscountTypeEnum }) => void;
  setSelectedPlan: (plan: PlanType) => void;
  subscription: ArtistSubscriptionFragment | gqlSubscription;
  artistId: string | number;
  plans?: PlanType[];
};

export const SpecialDiscountModal = ({ setSelectedPlan, showBillingPopup, subscription, artistId, plans }: Props) => {
  const { t } = useTranslation();
  const {
    isDiscountModalOpen,
    toggleDiscountModal,
    closeDiscountModal,
    bgColor,
    headerIcon,
    highlightColor,
    iconColor,
    offerTextProps,
    offerText,
    offerSubText1,
    offerSubText2,
    buttonColor,
    originalPrice,
    price,
    billedText,
    discountedPlan,
    discountBadgeText,
    discountBadgeIcon,
    planFeatures,
  } = useDiscountHook({ subscription, artistId, plans });

  return (
    <Modal
      title=""
      handleToggleClick={toggleDiscountModal}
      isOpen={isDiscountModalOpen}
      isFooterHidden
      isHeaderHidden
      modalSize="small"
      data-testid="special-discount-modal"
    >
      <FlexCol justifyItems="center" alignItems="center" gap="6" w="full" mt="8" mb="4">
        {discountBadgeText && (
          <FlexRow gap="2" alignItems="center" className="cn_atom_py-1 cn_atom_px-3 cn_shadow-md" borderRadius="full">
            {discountBadgeIcon && <IconWithErrorBoundary name={discountBadgeIcon} />}
            <BodyTextLarge color={highlightColor} fontWeight="semibold" italic>
              {discountBadgeText}
            </BodyTextLarge>
          </FlexRow>
        )}
        <IconWithErrorBoundary name={headerIcon} />
        {offerText && (
          <TextDynamic align="center" {...offerTextProps}>
            {offerText}
          </TextDynamic>
        )}
        <FlexRow
          bgColor={bgColor}
          borderRadius="md"
          className="cn_atom_p-2"
          gap="1"
          alignItems="center"
          justifyItems="center"
        >
          <H1Text color={highlightColor}>
            <IconWithErrorBoundary name="discount-tag" inheritColor className="cn_mx-2" />
            {t('common:paywall.plan.save_offer', { discountPercent: discountedPlan?.discountPercent })}
          </H1Text>
        </FlexRow>
        {(offerSubText1 || offerSubText2) && (
          <BasicContainer>
            {offerSubText1 && (
              <BodyTextLarge color="disabled" align="center">
                {offerSubText1}
              </BodyTextLarge>
            )}
            {offerSubText2 && (
              <BodyTextLarge color="disabled" align="center">
                {offerSubText2}
              </BodyTextLarge>
            )}
          </BasicContainer>
        )}
        <FlexCol w="full" justifyItems="center" alignItems="center" gap="2">
          <BodyTextLarge fontWeight="medium" as="span" textWrap="balance" align="center">
            <Trans
              i18nKey="common:paywall.plan.upgrade_premium_discount"
              components={[
                <BodyTextLarge fontWeight="medium" color="disabled" className="cn_line-through" as="span" />,
                <H3Text fontWeight="bold" color={highlightColor} as="span" />,
              ]}
              values={{
                originalPrice,
                price,
              }}
            />
          </BodyTextLarge>
          {billedText && <BodyTextSmall color="disabled">{billedText}</BodyTextSmall>}
        </FlexCol>
        <FlexCol w="full" gap="4" mdProps={{ w: '2/3' }}>
          {planFeatures?.map((feature) => (
            <FlexRow gap="3" alignItems="start" w="full" key={feature}>
              <IconWithErrorBoundary name="check-circle" themeColor={iconColor} />
              <BasicContainer w="4/5">
                <BodyTextSmall textWrap="balance">
                  <Trans i18nKey={feature} components={[<b />]} />
                </BodyTextSmall>
              </BasicContainer>
            </FlexRow>
          ))}
          <PaywallButton
            label={t('common:paywall.plan.discount.claim_offer')}
            color={buttonColor}
            onClick={() => {
              showBillingPopup({ location: PaywallLocation.DISCOUNT_MODAL });
              setSelectedPlan(discountedPlan);
              closeDiscountModal();
            }}
            full
            isSmall
          />
        </FlexCol>
      </FlexCol>
    </Modal>
  );
};
